// What a cell edit does to the setting values: the fill handle, writing one value to a range,
// pasting a column, and arrow-key navigation. timeSeriesSettingTable.helper.ts owns the rest.

import { TimeSeriesSettingValue } from 'api/digitalTwin/digitalTwin.api'

const FILL_HANDLE_MARKER = 'data-fill-handle'

/**
 * Marks the square in the bottom-right corner of an editable cell that a fill drag starts from.
 */
export const fillHandleMarkerProps = { [FILL_HANDLE_MARKER]: '' }

export const isFillHandleTarget = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest(`[${FILL_HANDLE_MARKER}]`) !== null

export const getRowIndexRange = (fromRowIndex: number, toRowIndex: number): number[] => {
  const first = Math.min(fromRowIndex, toRowIndex)
  const last = Math.max(fromRowIndex, toRowIndex)

  return Array.from({ length: last - first + 1 }, (_, step) => first + step)
}

/**
 * Splits clipboard text into one entry per cell.
 */
const getPastedCellValues = (clipboardText: string): string[] => {
  let values: string[] = []

  if (clipboardText.includes('\n')) {
    values = clipboardText.split(/\r?\n/).flatMap((line) => line.split('\t'))
  } else if (clipboardText.includes('\t')) {
    values = clipboardText.split('\t')
  } else {
    values = [clipboardText]
  }

  if (values.length > 1 && values[values.length - 1] === '') {
    values.pop()
  }

  return values
}

const parsePastedCellValue = (pastedValue: string): number | null => {
  if (pastedValue === '') {
    // Keep empty for alignment when pasting.
    return null
  }

  const parsedValue = parseFloat(pastedValue.replace(',', '.'))

  return isNaN(parsedValue) ? 0 : parsedValue
}

/**
 * Writes one value to the given rows, leaving every other row untouched. Backs every way a range is
 * filled — dragging the fill handle, typing into a selection, pasting a single value into one — so
 * they cannot drift apart.
 */
export const getSettingValuesWithValueAtRows = (
  settingValue: TimeSeriesSettingValue,
  times: ISODateTime[],
  rowIndices: number[],
  value: number | null
): TimeSeriesSettingValue => {
  const newSettingValues = { ...settingValue }

  rowIndices.forEach((rowIndex) => {
    const time = times[rowIndex]

    if (time === undefined) {
      return
    }

    newSettingValues[time] = { id: newSettingValues[time]?.id, value }
  })

  return newSettingValues
}

/**
 * Where pasted clipboard text lands. One value pasted into a cell of the selection fills the whole
 * selection; anything else is a column copied from a spreadsheet and lands row by row from the cell
 * that was pasted into, which is the only reading that keeps a copied column aligned.
 */
export const getSettingValuesFromPaste = ({
  settingValue,
  times,
  clipboardText,
  targetRowIndex,
  selectedRows,
}: {
  settingValue: TimeSeriesSettingValue
  times: ISODateTime[]
  clipboardText: string
  targetRowIndex: number
  selectedRows: number[]
}): TimeSeriesSettingValue => {
  const values = getPastedCellValues(clipboardText)

  if (values.length === 1 && selectedRows.length > 1 && selectedRows.includes(targetRowIndex)) {
    return getSettingValuesWithValueAtRows(settingValue, times, selectedRows, parsePastedCellValue(values[0]))
  }

  const newSettingValues = { ...settingValue }

  values.forEach((pastedValue, offset) => {
    const time = times[targetRowIndex + offset]

    if (time === undefined) {
      return
    }

    newSettingValues[time] = { id: newSettingValues[time]?.id, value: parsePastedCellValue(pastedValue) }
  })

  return newSettingValues
}

const ARROW_KEY_CODES = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']

/**
 * Where an arrow key moves the focused cell, clamped to the page so navigation stops at its edges
 * rather than wrapping. Returns undefined for any other key.
 */
export const getArrowKeyFocusTarget = ({
  key,
  rowIndex,
  columnId,
  datasets,
  rowsOnPage,
}: {
  key: string
  rowIndex: number
  columnId: string
  datasets: Dataset[]
  rowsOnPage: number
}): { row: number; column: string } | undefined => {
  if (!ARROW_KEY_CODES.includes(key)) {
    return undefined
  }

  const columnIndex = datasets.findIndex((dataset) => dataset.return_id === columnId)

  if (key === 'ArrowUp') {
    return { row: Math.max(0, rowIndex - 1), column: columnId }
  }

  if (key === 'ArrowDown') {
    return { row: Math.min(rowsOnPage - 1, rowIndex + 1), column: columnId }
  }

  const nextColumnIndex =
    key === 'ArrowLeft' ? Math.max(0, columnIndex - 1) : Math.min(datasets.length - 1, columnIndex + 1)

  return { row: rowIndex, column: datasets[nextColumnIndex].return_id }
}
